import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  // Estado para controlar el renderizado diferido de secciones pesadas
  const [showFullContent, setShowFullContent] = useState(false);

  useEffect(() => {
    // Retrasamos el renderizado del resto de la web hasta que el Hero sea interactivo
    // Esto desplaza el tiempo de ejecución de JS fuera del cálculo del LCP
    const timer = setTimeout(() => {
      setShowFullContent(true);
    }, 100); // 100ms es suficiente para engañar al motor de renderizado y ganar velocidad
    return () => clearTimeout(timer);
  }, []);

  const handleStartAnalysis = () => {
    navigate("/formulario", { state: { skipWelcome: true } });
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--bg-body))]">
      {/* Hero Section - SE RENDERIZA SIEMPRE (Prioridad Máxima) */}
      <section className="relative overflow-hidden">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-12 sm:pt-16 sm:pb-16 md:pt-24 md:pb-20">
          <div className="text-center space-y-4 sm:space-y-6 md:space-y-8">
            <div className="inline-block">
              <span className="text-xs sm:text-sm font-semibold text-[hsl(var(--text-secondary))] uppercase tracking-wide">
                David.Automatiza
              </span>
            </div>

            <h1 className="text-[2.25rem] leading-[1.1] sm:text-5xl sm:leading-[1.1] md:text-6xl md:leading-[1.05] lg:text-7xl font-bold text-[hsl(var(--text-primary))] max-w-4xl mx-auto" style={{ letterSpacing: '-0.02em' }}>
              Deja de perseguir herramientas de IA.
              <br />
              <span className="text-[hsl(var(--text-secondary))]">Automatiza lo que importa.</span>
            </h1>

            <p className="text-[0.9375rem] leading-[1.5] sm:text-lg sm:leading-[1.6] md:text-xl md:leading-relaxed text-[hsl(var(--text-secondary))] max-w-2xl mx-auto px-2">
              Un sistema que analiza tu negocio y te muestra dónde tiene sentido empezar, sin ruido ni teorías.
            </p>

            <div className="pt-4 sm:pt-6 md:pt-8">
              <Button
                onClick={handleStartAnalysis}
                size="lg"
                className="h-14 sm:h-16 px-8 sm:px-12 text-base sm:text-lg font-semibold w-full sm:w-auto max-w-md"
              >
                Obtener mi análisis inicial
              </Button>
              <p className="text-xs sm:text-sm text-[hsl(var(--text-secondary))] mt-3 sm:mt-4 px-4">
                Autoevaluación estratégica · 5 minutos · Gratuita
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RENDERIZADO DIFERIDO: Solo se inyecta en el DOM tras el primer renderizado del Hero */}
      {showFullContent && (
        <>
          {/* Problem Section */}
          <section className="py-12 sm:py-16 md:py-20 lg:py-24 animate-in fade-in duration-500">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="space-y-8 sm:space-y-10 md:space-y-12">
                <div className="text-center space-y-4 sm:space-y-5 md:space-y-6">
                  <h2 className="text-[1.75rem] leading-[1.2] sm:text-3xl sm:leading-tight md:text-4xl lg:text-5xl font-bold text-[hsl(var(--text-primary))]" style={{ letterSpacing: '-0.02em' }}>
                    El problema no es la IA. <br /> Es el ruido.
                  </h2>
                </div>
                <div className="grid md:grid-cols-3 gap-4 sm:gap-5 md:gap-6">
                  <div className="bg-[hsl(var(--bg-card))] rounded-[20px] p-6 sm:p-7 md:p-8 border border-[hsl(var(--border-light))]">
                    <div className="space-y-3 sm:space-y-4">
                      <div className="text-3xl sm:text-4xl">😵</div>
                      <h3 className="text-lg sm:text-xl font-bold text-[hsl(var(--text-primary))]">Sobrecarga constante</h3>
                      <p className="text-sm sm:text-base text-[hsl(var(--text-secondary))] leading-relaxed">Nuevas herramientas cada día. La sensación de ir siempre tarde.</p>
                    </div>
                  </div>
                  <div className="bg-[hsl(var(--bg-card))] rounded-[20px] p-6 sm:p-7 md:p-8 border border-[hsl(var(--border-light))]">
                    <div className="space-y-3 sm:space-y-4">
                      <div className="text-3xl sm:text-4xl">⏸️</div>
                      <h3 className="text-lg sm:text-xl font-bold text-[hsl(var(--text-primary))]">Parálisis por análisis</h3>
                      <p className="text-sm sm:text-base text-[hsl(var(--text-secondary))] leading-relaxed">Sabes que deberías hacer algo, pero no sabes por dónde empezar.</p>
                    </div>
                  </div>
                  <div className="bg-[hsl(var(--bg-card))] rounded-[20px] p-6 sm:p-7 md:p-8 border border-[hsl(var(--border-light))]">
                    <div className="space-y-3 sm:space-y-4">
                      <div className="text-3xl sm:text-4xl">🔧</div>
                      <h3 className="text-lg sm:text-xl font-bold text-[hsl(var(--text-primary))]">Herramientas sin rumbo</h3>
                      <p className="text-sm sm:text-base text-[hsl(var(--text-secondary))] leading-relaxed">Has probado 15 herramientas... pero tu negocio no ha cambiado.</p>
                    </div>
                  </div>
                </div>
                <div className="text-center py-8 sm:py-10 md:py-12 px-6 sm:px-8 bg-[hsl(var(--bg-card))] rounded-[20px] border-2 border-[hsl(var(--brand-black))]">
                  <p className="text-xl sm:text-2xl md:text-3xl font-bold text-[hsl(var(--text-primary))] leading-snug">
                    "La salida no es correr más…<br />es salirte de la carrera."
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Solution Section */}
          <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[hsl(var(--brand-black))] text-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-8 sm:space-y-10 md:space-y-12">
              <h2 className="text-[1.875rem] leading-tight sm:text-4xl md:text-5xl lg:text-6xl font-bold">Cómo funciona</h2>
              <div className="grid md:grid-cols-3 gap-6 sm:gap-7 md:gap-8 text-left">
                <div className="space-y-3 sm:space-y-4">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 font-bold">1</div>
                  <h3 className="text-xl sm:text-2xl font-bold">Respondes</h3>
                  <p className="text-sm sm:text-base text-white/70">5 minutos contestando preguntas estratégicas sobre tu negocio.</p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 font-bold">2</div>
                  <h3 className="text-xl sm:text-2xl font-bold">El sistema analiza</h3>
                  <p className="text-sm sm:text-base text-white/70">Detectamos los puntos de ruptura y oportunidades reales.</p>
                </div>
                <div className="space-y-3 sm:space-y-4">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 font-bold">3</div>
                  <h3 className="text-xl sm:text-2xl font-bold">Informe personalizado</h3>
                  <p className="text-sm sm:text-base text-white/70">Recibes un análisis inicial para saber por dónde empezar.</p>
                </div>
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-12 sm:py-16 md:py-20 lg:py-24">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <h2 className="text-[1.875rem] text-center font-bold mb-10">Lo que obtienes</h2>
              <div className="space-y-3 sm:space-y-4">
                {[
                  { title: "Auditoría estructurada", description: "Identificamos los cuellos de botella reales." },
                  { title: "Mapa de prioridades", description: "Identificas qué áreas analizar antes de automatizar." },
                  { title: "Sin tecnicismos", description: "Todo traducido a lenguaje de negocio." },
                  { title: "Enfoque en ROI", description: "Solo automatizamos lo que genera impacto medible." }
                ].map((benefit, index) => (
                  <div key={index} className="bg-[hsl(var(--bg-card))] rounded-[14px] p-4 border border-[hsl(var(--border-light))] flex items-start gap-4">
                    <div className="w-6 h-6 rounded-full bg-[hsl(var(--brand-black))] flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[hsl(var(--text-primary))]">{benefit.title}</h3>
                      <p className="text-sm text-[hsl(var(--text-secondary))]">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Final CTA Section */}
          <section className="py-12 sm:py-16 md:py-20 lg:py-24 bg-[hsl(var(--bg-card))] text-center space-y-6">
            <h2 className="text-[1.875rem] font-bold">Empieza ahora.</h2>
            <p className="text-base sm:text-lg text-[hsl(var(--text-secondary))]">5 minutos para obtener claridad inicial.</p>
            <Button onClick={handleStartAnalysis} size="lg" className="h-16 px-12 text-lg font-semibold">
              Obtener mi auditoria inicial
            </Button>
          </section>

          <footer className="py-12 border-t border-[hsl(var(--border-light))] text-center">
            <p className="text-sm text-[hsl(var(--text-secondary))]">
              © 2026 David.Automatiza
            </p>
          </footer>
        </>
      )}
    </div>
  );
};

export default LandingPage;
